/**
 * RequireOnShift.jsx
 * CCH Care — Phase 2 (Shift-Based Access Control)
 * Last Modified: 2026-05-13
 *
 * Router-level guard that enforces shift-based access control on
 * clinical workflow routes per Policy v1.0 §4.2 and §4.8.
 *
 * Behavior by useShift().shiftState:
 *   loading   → render a small loader (don't redirect mid-decision)
 *   no_shift  → redirect to /dashboard (no clinical work without an assignment)
 *   upcoming  → allow through (pre-shift window — read-only access)
 *   active    → allow through (full access — banner shown if in grace window)
 *   ended     → redirect to /dashboard (grace window expired, hard lockout)
 *   error     → allow through (transient errors shouldn't lock users out;
 *                downstream API gates still enforce per-endpoint access)
 *
 * Bypass: users with patients.view_all bypass the gate entirely. Care
 * Supervisors, Care Admins, Physicians, and Nurses use this app outside
 * the strict roster (float view, oversight, off-hours review) and
 * shouldn't be blocked by shift end. CNAs lack this permission and are
 * subject to the gate. Tenant admins bypass via useAuth's is_tenant_admin
 * short-circuit on hasPermission().
 *
 * Supports two usage modes:
 *
 *   1. Wrapper:
 *        <RequireOnShift>
 *          <SomePage />
 *        </RequireOnShift>
 *
 *   2. Layout route (preferred for wrapping multiple routes at once):
 *        <Route element={<RequireOnShift />}>
 *          <Route path="adl" element={<ADLFlowSheetPage />} />
 *          <Route path="mar" element={<MARPage />} />
 *        </Route>
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import useShift from '../../hooks/useShift';
import { useAuth } from '../../features/auth/useAuth';
import GraceWindowBanner from '../shift/GraceWindowBanner';
import { COLORS } from '../../config/constants';

export default function RequireOnShift({ children }) {
  const { shift, shiftState, inGraceWindow, loading: shiftLoading } = useShift();
  const { hasPermission } = useAuth();
  const location = useLocation();

  // Bypass for roles whose work isn't tied to a specific shift assignment.
  // Same permission gate used by residents/directory.php on the backend.
  const bypassesGate = hasPermission('patients.view_all');

  // Loading: don't redirect mid-decision. A brief "Checking shift..."
  // state is preferable to a flicker through the gate.
  if (shiftLoading || shiftState === 'loading') {
    return (
      <div style={{
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: COLORS.bg,
      }}>
        <div style={{
          textAlign: 'center',
          color: COLORS.textMuted,
          fontSize: 14,
        }}>
          <div style={{
            width: 32, height: 32, margin: '0 auto 12px',
            border: `3px solid ${COLORS.border}`,
            borderTopColor: COLORS.primary,
            borderRadius: '50%',
            animation: 'rosShiftSpin 0.8s linear infinite',
          }} />
          Checking shift…
          <style>{`@keyframes rosShiftSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Hard lockout — only when the user lacks the bypass permission.
  // Redirect target is the dashboard; once there, ShiftDashboard renders
  // the appropriate empty state (ended / no_shift) with the right copy
  // and any review actions (Review Shift Log, Add Note, etc.).
  if (!bypassesGate && (shiftState === 'ended' || shiftState === 'no_shift')) {
    return <Navigate to="/dashboard" replace state={{ from: location }} />;
  }

  // Allow through. Banner mounts during the grace window so the user
  // sees a clear indicator that they're documenting late, regardless
  // of which clinical route they're on.
  return (
    <>
      {inGraceWindow && <GraceWindowBanner shift={shift} />}
      {children ?? <Outlet />}
    </>
  );
}